/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'

import { EuiIcon, EuiLoadingSpinner, EuiToolTip } from '@elastic/eui'

import type { AnyResourceInfo, AnyClusterPlanInfo, SliderInstanceType } from '@modules/ui-types'

import { isPendingAttempt } from '@/lib/stackDeployments/selectors/configurationChangeAttempts'
import { isDryRunAttempt } from '@/lib/healthProblems/stackDeploymentHealth/configurationChangeError'
import { parseConfigurationChangeError } from '@/lib/healthProblems/stackDeploymentHealth'

import type { FunctionComponent } from 'react'

const tooltipStyles = css({
  maxWidth: '500px',
})

type Props = {
  resource: AnyResourceInfo
  resourceType: SliderInstanceType
  planAttempt: AnyClusterPlanInfo
  concealPendingPlan?: boolean
}

const StackConfigurationChangeStatus: FunctionComponent<Props> = ({
  resource,
  resourceType,
  planAttempt,
  concealPendingPlan,
}) => {
  if (isPendingAttempt({ planAttempt }) && !concealPendingPlan) {
    return <EuiLoadingSpinner data-test-id='plan-attempt-spinner' size='m' />
  }

  const { healthy } = planAttempt

  const isDryRun = isDryRunAttempt({ planAttempt })

  const errorMessage = parseConfigurationChangeError({ resource, resourceType, planAttempt })
  const icon = <EuiIcon type='dot' size='l' color={healthy || isDryRun ? `success` : `danger`} />

  if (errorMessage === null) {
    return icon
  }

  const { title, description } = errorMessage
  const tooltipContent = description || title

  return (
    <EuiToolTip css={tooltipStyles} content={tooltipContent}>
      <div>{icon}</div>
    </EuiToolTip>
  )
}

export default StackConfigurationChangeStatus
